import { Button, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import React, { useState } from 'react';
import Colors from '../../utils/color';
import { ParsedCategory } from '../../utils/menu';
import useEventListener from '../../hooks/useEventListener.hooks';
import useBanners from './useBanners.hooks';
import { ArrowKeys } from '../../constants/enums';

interface INotificationProps {
  isExpanded: boolean;
}

interface IBannerProps {
  // When true, render only the notifications (no EXPAND/CLOSE button) in a
  // static, transparent layout suitable for embedding inside another card.
  embedded?: boolean;
}

const useStyles = makeStyles<Theme, INotificationProps>(({ spacing }) => ({
  notificationClass: {
    maxHeight: ({ isExpanded }) => (isExpanded ? 'none' : spacing(7)),
    background: Colors.spindle,
    padding: `${spacing(1)} ${spacing(9)} ${spacing(1)} ${spacing(1)}`,
    overflowY: 'hidden',
    overflowX: 'auto',
    position: 'absolute !important' as any,
    width: '100%',
    zIndex: 2,
  },
  buttonClass: {
    color: `${Colors.bluishCyan} !important`,
    position: 'absolute !important' as any,
    top: 0,
    right: 0,
    '&:hover': {
      backgroundColor: `transparent !important`,
    },
    '&> .MuiTouchRipple-root span': {
      backgroundColor: `transparent !important`,
    },
  },
}));

enum SizeOptions {
  Expand = 'EXPAND',
  Close = 'CLOSE',
}

// Embedded layout (inside another card, e.g. the Current Step card): static,
// transparent, no EXPAND/CLOSE button. Applied via `sx` rather than a
// makeStyles rule so Banner's stylesheet stays identical to the non-embedded
// (default) build — only the embedded callers opt in.
const embeddedSx = {
  background: 'transparent',
  width: '100%',
  // Show ~2 lines (0.75rem * 1.5 line-height ≈ 18px each); scroll past that.
  maxHeight: 36,
  overflowY: 'auto',
  overflowX: 'hidden',
  '& .MuiTypography-root': {
    fontSize: '0.75rem',
  },
} as const;

const Banner = ({ embedded = false }: IBannerProps) => {
  const banners = useBanners();

  const [size, setSize] = useState(
    banners.length > 2 ? SizeOptions.Expand : ''
  );

  const { notificationClass, buttonClass } = useStyles({
    isExpanded: size === SizeOptions.Close,
  });

  const handleKeyUp = (event: Event) => {
    if (
      event instanceof KeyboardEvent &&
      event.key === ArrowKeys.arrowLeft &&
      event.shiftKey
    ) {
      event.preventDefault();
      event.stopPropagation();
      handleSizeChange();
    }
  };
  useEventListener('keyup', handleKeyUp);

  if (!banners.length) return null;

  const handleSizeChange = () => {
    setSize(
      size === SizeOptions.Close ? SizeOptions.Expand : SizeOptions.Close
    );
  };

  const getNotification = (category: ParsedCategory) => {
    const activeTimePeriod =
      category.activeTimePeriod && !category.activeTimePeriod.alwaysEnabled
        ? ` (${category.activeTimePeriod.start}-${category.activeTimePeriod.end} ${category.activeTimePeriod.day})`
        : '';
    return (
      <Typography
        key={category.id}
        sx={{ lineHeight: 1.5, fontWeight: 'normal' }}
      >
        {category.name + activeTimePeriod}
      </Typography>
    );
  };

  return (
    <Box
      className={embedded ? undefined : notificationClass}
      sx={embedded ? embeddedSx : undefined}
    >
      {banners.map((banner) => getNotification(banner))}
      {!embedded && size ? (
        <Button className={buttonClass} onClick={handleSizeChange}>
          {size}
        </Button>
      ) : null}
    </Box>
  );
};

export default Banner;
